import React, { useContext, useCallback, useRef, useEffect } from 'react';
import { Session } from '@app/api';
import { useBoolModalState } from '@app/core';
import { PlanBillingModal } from './PlanBillingModal';

interface Props {
  children: React.ReactNode;
  session: Session;
  showAllPriceInfo?: boolean;
  duringSessionProcess?: boolean;
}

const Context = React.createContext<() => Promise<void>>(async () => {});

export const useRequestBillingInformation = () => useContext(Context);

export const BillingInformationModalContext = ({
  children,
  ...props
}: Props) => {
  const [isOpen, open, close] = useBoolModalState(false);

  const pendingRequestsRef = useRef<
    Array<{ resolve: () => any; reject: () => any }>
  >([]);

  const requestBillingInformation = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      open();
      pendingRequestsRef.current.push({ resolve, reject });
    });
  }, [open, pendingRequestsRef]);

  const onRequestClose = useCallback(() => {
    close();
    pendingRequestsRef.current.forEach(({ resolve }) => {
      resolve();
    });
    pendingRequestsRef.current = [];
  }, [pendingRequestsRef, close]);

  useEffect(() => {
    return () => {
      pendingRequestsRef.current.forEach(({ reject }) => {
        reject();
      });
      pendingRequestsRef.current = [];
    };
  }, [pendingRequestsRef]);

  return (
    <>
      <Context.Provider value={requestBillingInformation}>
        {children}
      </Context.Provider>
      <PlanBillingModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        {...props}
      />
    </>
  );
};
